/**
 * Compact countdown pills for home boards (task #89). Pulls from Kim's
 * countdownEvents list (Studio Settings) and shows the next few upcoming
 * dates as small non-interactive pills: emoji + name + "in N days".
 *
 * Visibility is a client-side display filter (Firestore read is open to any
 * active signed-in user, see lib/countdownEvents.ts):
 * - 'everyone' shows to everyone.
 * - 'comp' shows to staff roles and to family accounts with a comp dancer.
 * - 'staff' shows only to staff roles.
 *
 * Fails silent on this page - no error box on the home board, just render
 * nothing if there's nothing to show or something's wrong.
 */

import { Link } from 'react-router-dom';
import {
  countdownAudienceAllows,
  countdownDateRangeLabel,
  countdownEventHref,
  countdownRunningLabel,
  daysUntil,
  isCountdownRange,
  type CountdownEventDoc,
  useCountdownEvents,
  homeCountdownEvents,
} from '../lib/countdownEvents';

const STAFF_ROLES = new Set(['director', 'manager', 'company', 'ensemble', 'foh']);

const MAX_PILLS = 4;

function daysLabel(days: number): string {
  if (days <= 0) return 'today!';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

export function CountdownPills({
  role,
  hasCompDancer,
}: {
  role: string | undefined;
  hasCompDancer?: boolean;
}) {
  const { events, loading, error } = useCountdownEvents();

  if (loading || error) return null;

  const isStaff = !!role && STAFF_ROLES.has(role);
  const upcoming = homeCountdownEvents(events);
  const visible = upcoming.filter((event) =>
    countdownAudienceAllows(event, { isStaff, hasCompDancer })
  );

  if (visible.length === 0) return null;

  const sorted = [...visible].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const shown = sorted.slice(0, MAX_PILLS);
  const extra = sorted.length - shown.length;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {shown.map((event) => (
        <CountdownPill
          key={event.id}
          event={event}
          // A caller that SAYS the family has no comp dancer gets the
          // generic event page for a competition, not the comp team's door
          // they would be turned away at. A caller that does not say keeps
          // the comp page, the answer this pill always gave.
          compPage={isStaff || hasCompDancer !== false}
        />
      ))}
      {extra > 0 && (
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)' }}>
          +{extra} more
        </span>
      )}
    </div>
  );
}

function CountdownPill({ event, compPage }: { event: CountdownEventDoc; compPage: boolean }) {
  const days = daysUntil(event.date);
  // Bobbie, 2026-08-27: "when you click on the [pill] button as a parent or
  // staff for an important date it should take you to the page/event for it if
  // there is one!" A competition has its own page; everything else opens its
  // own event page at /events/:id. The destination re-runs the same audience
  // check the pill did, so a link cannot widen who sees what.
  const href = countdownEventHref(event, { compPage });
  // A multi-day event reads as its dates ("Sep 15-19"), or as "Happening now"
  // once it has actually started; a single date keeps the countdown phrasing
  // it has always had. Days-to-go is always measured to the START of a range,
  // which is why a running range never renders one.
  const timing =
    countdownRunningLabel(event)
    ?? (isCountdownRange(event) ? countdownDateRangeLabel(event) : daysLabel(days));
  const body = (
    <>
      <span aria-hidden="true">{event.emoji}</span>
      <span>{event.name}</span>
      <span style={{ color: 'var(--ink-soft)', fontWeight: 700 }}>{timing}</span>
    </>
  );
  const pillStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    // 44 when it is a real tap target, 32 when it is only a label.
    minHeight: href ? 44 : 32,
    padding: '6px 12px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    color: 'var(--ink)',
    background: `${event.color}22`,
    borderLeft: `3px solid ${event.color}`,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  };
  if (!href) return <span style={pillStyle}>{body}</span>;
  return (
    <Link to={href} style={pillStyle} aria-label={`${event.name}, ${timing}`}>
      {body}
    </Link>
  );
}

export default CountdownPills;
